/**
 * EVERY TEST ID THE CONSOLE RENDERS, DECLARED ONCE (CONTRACT-console S-invariant, "targets declared once").
 *
 * A component renders a test id only from this module: a fixed id from `TEST_IDS`, a templated one through
 * `testIdOf` (or a `testIdFor` builder over it). Journey tests and live plans address the page through
 * the same names, and the live runner (`scripts/run-live-journeys.mjs`, plain ESM) reads the generated
 * `scripts/testdata/test-ids.json` that `npm run test-ids:gen` writes from this file. Renaming a key breaks
 * the typecheck of every component, test and plan that uses it; renaming a value moves all of them at once;
 * a component that stops using a declared id fails `npm run test-ids:check`. So a plan cannot wait on a
 * test id no component renders any more, which is how three live-gate journeys went stale in 1.7.38.
 *
 * The file is data plus two pure functions (`testIdSlug`, `fillTestId`), which the runner restates and
 * `scripts/run-live-journeys.test.ts` pins to the same answers.
 */

export const TEST_IDS = {
  accountEnvironment: 'account-environment',
  audioFrameRate: 'audio-frame-rate',
  audioHistoryPreroll: 'audio-history-preroll',
  audioInput: 'audio-input',
  audioInputClipping: 'audio-input-clipping',
  audioInputInstrument: 'audio-input-instrument',
  audioInputRaw: 'audio-input-raw',
  audioLevelHistory: 'audio-level-history',
  audioPeakDb: 'audio-peak-db',
  audioRenderRateWarning: 'audio-render-rate-warning',
  audioRmsDb: 'audio-rms-db',
  audioStack: 'audio-stack',
  audioStackBaselineEffect: 'audio-stack-baseline-effect',
  audioStackLayers: 'audio-stack-layers',
  audioStackReactiveAdd: 'audio-stack-reactive-add',
  audioStackSettings: 'audio-stack-settings',
  audioTempo: 'audio-tempo',
  audioTempoLocked: 'audio-tempo-locked',
  autoVjAddSet: 'auto-vj-add-set',
  autoVjAddStep: 'auto-vj-add-step',
  autoVjAddUnit: 'auto-vj-add-unit',
  autoVjEditor: 'auto-vj-editor',
  autoVjEmptySet: 'auto-vj-empty-set',
  autoVjFeedback: 'auto-vj-feedback',
  autoVjFeedbackDetails: 'auto-vj-feedback-details',
  autoVjLayoutLoading: 'auto-vj-layout-loading',
  autoVjLayoutMissing: 'auto-vj-layout-missing',
  autoVjSaveDetails: 'auto-vj-save-details',
  autoVjSaveState: 'auto-vj-save-state',
  autoVjSetOpen: 'auto-vj-set-open',
  autoVjUnavailable: 'auto-vj-unavailable',
  backToSignIn: 'back-to-sign-in',
  bluetoothControlCode: 'bluetooth-control-code',
  bluetoothControlState: 'bluetooth-control-state',
  ceilingValue: 'ceiling-value',
  changeWifi: 'change-wifi',
  confirmAccept: 'confirm-accept',
  confirmCancel: 'confirm-cancel',
  confirmModal: 'confirm-modal',
  connectBluetooth: 'connect-bluetooth',
  connectRigSummary: 'connect-rig-summary',
  connectionState: 'connection-state',
  controlFeedbackSlot: 'control-feedback-slot',
  controllerNameInput: 'controller-name-input',
  controllerNetwork: 'controller-network',
  createAccount: 'create-account',
  effectCardGrid: 'effect-card-grid',
  effectLayoutMissing: 'effect-layout-missing',
  effectPackagePanel: 'effect-package-panel',
  effectPackageParameters: 'effect-package-parameters',
  effectPackageUnavailable: 'effect-package-unavailable',
  effectParameters: 'effect-parameters',
  effectRatePrefix: 'effect',
  effectRoomCanvas: 'effect-room-canvas',
  effectStatusProblem: 'effect-status-problem',
  effectsFilterAll: 'effects-filter-all',
  effectsFilterMusic: 'effects-filter-music',
  effectsRoomLive: 'effects-room-live',
  email: 'email',
  fixtureIntensity: 'fixture-intensity',
  globalBlackout: 'global-blackout',
  goCreate: 'go-create',
  goReset: 'go-reset',
  gradientApply: 'gradient-apply',
  gradientAxisRows: 'gradient-axis-rows',
  gradientBar: 'gradient-bar',
  gradientEditor: 'gradient-editor',
  gradientToggle: 'gradient-toggle',
  intensityLimitConfirm: 'intensity-limit-confirm',
  intensityLimitConfirmAccept: 'intensity-limit-confirm-accept',
  intensityLimitConfirmWord: 'intensity-limit-confirm-word',
  joinWifi: 'join-wifi',
  maxIntensity: 'max-intensity',
  memberAdd: 'member-add',
  memberAddRole: 'member-add-role',
  memberEmail: 'member-email',
  memberList: 'member-list',
  memberRoleDescriptions: 'member-role-descriptions',
  membersCard: 'members-card',
  musicInputNeeded: 'music-input-needed',
  musicInputState: 'music-input-state',
  musicListening: 'music-listening',
  musicTelemetry: 'music-telemetry',
  name: 'name',
  nowPlaying: 'now-playing',
  nowPlayingName: 'now-playing-name',
  operatorActivity: 'operator-activity',
  outputViewContextIndicator: 'output-view-context-indicator',
  outputViewExtended: 'output-view-extended',
  outputViewHolder: 'output-view-holder',
  outputViewReason: 'output-view-reason',
  outputViewSessionExtended: 'output-view-session-extended',
  outputViewStageIndicator: 'output-view-stage-indicator',
  outputViewStart: 'output-view-start',
  outputViewStop: 'output-view-stop',
  outputViewTakeover: 'output-view-takeover',
  outputViewWire: 'output-view-wire',
  pairLan: 'pair-lan',
  pairSetupCode: 'pair-setup-code',
  password: 'password',
  playheadScrubber: 'playhead-scrubber',
  remoteControlEnabled: 'remote-control-enabled',
  resendVerification: 'resend-verification',
  resetEffectPackage: 'reset-effect-package',
  rigMapReloaded: 'rig-map-reloaded',
  roomCanvas: 'room-canvas',
  runningEffect: 'running-effect',
  saveMaxIntensity: 'save-max-intensity',
  sceneLibrary: 'scene-library',
  sceneLibraryScroll: 'scene-library-scroll',
  scoreBars: 'score-bars',
  scoreDelete: 'score-delete',
  scoreList: 'score-list',
  scorePlay: 'score-play',
  scoreVersion: 'score-version',
  selectionAttribution: 'selection-attribution',
  selectionControl: 'selection-control',
  selectionCount: 'selection-count',
  selectionLevelSummary: 'selection-level-summary',
  sendReset: 'send-reset',
  settingsSheet: 'settings-sheet',
  settingsSheetBack: 'settings-sheet-back',
  settingsSheetClose: 'settings-sheet-close',
  settingsSheetDetail: 'settings-sheet-detail',
  settingsSheetSave: 'settings-sheet-save',
  settingsSheetScrim: 'settings-sheet-scrim',
  settingsTree: 'settings-tree',
  settingsTreeSearch: 'settings-tree-search',
  signIn: 'sign-in',
  songChangeAge: 'song-change-age',
  stagedEditsNotice: 'staged-edits-notice',
  startAutoVj: 'start-auto-vj',
  startEffect: 'start-effect',
  startEffectPackage: 'start-effect-package',
  stopAutoVj: 'stop-auto-vj',
  stopEffect: 'stop-effect',
  stopEffectPackage: 'stop-effect-package',
  streamingDisconnect: 'streaming-disconnect',
  streamingIndicator: 'streaming-indicator',
  takeOverScenes: 'take-over-scenes',
  transportStop: 'transport-stop',
  transportToggle: 'transport-toggle',
  wifiCard: 'wifi-card',
  wifiSetupCode: 'wifi-setup-code',
  wifiStatus: 'wifi-status',
} as const;

/** Templated test ids: `{slot}` is filled by `fillTestId`, never by string concatenation elsewhere. */
export const TEST_ID_TEMPLATES = {
  musicChannelGain: 'music-channel-{channel}-gain',
  musicChannelDenoise: 'music-channel-{channel}-denoise',
  musicChannelMeter: 'music-channel-{channel}-meter',
  effectCard: 'select-{package}',
  effectCardPlay: 'play-{package}',
  audioLayer: 'audio-stack-{layer}',
  audioStackMix: 'audio-stack-mix-{mix}',
  autoVJSet: 'auto-vj-set-{set}',
  autoVJStep: 'auto-vj-step-{step}',
  settingsNode: 'settings-{node}',
  settingsDetail: 'settings-detail-{node}',
  settingsTreeNode: 'settings-tree-node-{node}',
  settingsTreeChevron: 'settings-tree-chevron-{node}',
  settingsTreePlus: 'settings-tree-plus-{node}',
  member: 'member-{uid}',
  memberRole: 'member-role-{uid}',
  memberRemove: 'member-remove-{uid}',
  invite: 'invite-{key}',
  inviteRemove: 'invite-remove-{key}',
  control: '{prefix}-{key}',
  controlValue: '{prefix}-value-{key}',
  controlOption: '{prefix}-{key}-{value}',
  controlItem: '{prefix}-{key}-item-{index}',
  controlItemUp: '{prefix}-{key}-up-{index}',
  controlItemDown: '{prefix}-{key}-down-{index}',
  controlItemRemove: '{prefix}-{key}-remove-{index}',
  controlItemAdd: '{prefix}-{key}-add',
  controlRow: '{prefix}-row-{key}',
  controlError: '{prefix}-error-{key}',
  presets: '{prefix}-presets',
  presetList: '{prefix}-preset-list',
  presetName: '{prefix}-preset-name',
  presetSave: '{prefix}-preset-save',
  preset: '{prefix}-preset-{preset}',
  presetApply: '{prefix}-preset-apply-{preset}',
  presetRename: '{prefix}-preset-rename-{preset}',
  presetRenameName: '{prefix}-preset-rename-name',
  presetDelete: '{prefix}-preset-delete-{preset}',
  layerEffect: '{prefix}-effect',
  layerDriver: '{prefix}-driver-{source}',
  layerSensitivity: '{prefix}-sensitivity',
  layerThreshold: '{prefix}-threshold',
  layerAmount: '{prefix}-amount',
  layerAttack: '{prefix}-attack',
  layerRelease: '{prefix}-release',
  musicRate: '{prefix}-music-rate',
  musicRateOption: '{prefix}-music-rate-{rate}',
  sync: '{prefix}-sync',
  syncControl: '{prefix}-sync-control',
  syncStatus: '{prefix}-sync-status',
} as const;

export type TestIdTemplate = keyof typeof TEST_ID_TEMPLATES;
type Slots<T extends string> = T extends `${string}{${infer Slot}}${infer Rest}` ? Slot | Slots<Rest> : never;
export type TestIdSlots<K extends TestIdTemplate> = Record<Slots<(typeof TEST_ID_TEMPLATES)[K]>, string | number>;

/** A value as it may appear inside a test id: every run of other characters becomes one `-`, lower case. */
export function testIdSlug(value: string): string {
  return value.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
}

/** Fill a template's `{slot}`s. A slot left unfilled throws: a half-built id addresses nothing. */
export function fillTestId(template: string, slots: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{([a-z]+)\}/gi, (_match, slot: string) => {
    if (!(slot in slots)) throw new Error(`test id template ${template} has no value for {${slot}}`);
    return String(slots[slot]);
  });
}

/** The fixed text a template starts with, before its first slot (for `^=` prefix selectors). */
export function testIdTemplateHead(name: TestIdTemplate): string {
  const template: string = TEST_ID_TEMPLATES[name];
  return template.slice(0, template.indexOf('{'));
}

/** The test id a named template renders with these slot values. */
export function testIdOf<K extends TestIdTemplate>(name: K, slots: TestIdSlots<K>): string {
  return fillTestId(TEST_ID_TEMPLATES[name], slots as Record<string, string | number>);
}

/** The builders components and tests share for ids whose slots are derived from a domain id. */
export const testIdFor = {
  /** Every control of one effect package shares this prefix (its package id, slugged). */
  packagePrefix: (packageId: string): string => testIdSlug(packageId),
  effectCard: (packageId: string): string => testIdOf('effectCard', { package: testIdSlug(packageId) }),
  effectCardPlay: (packageId: string): string => testIdOf('effectCardPlay', { package: testIdSlug(packageId) }),
  /** Every control of one Audio reactive layer shares this prefix; it is also the layer's pane row. */
  audioLayer: (layerId: string): string => testIdOf('audioLayer', { layer: testIdSlug(layerId) }),
  autoVJSet: (setId: string): string => testIdOf('autoVJSet', { set: testIdSlug(setId) }),
  autoVJStep: (stepId: string): string => testIdOf('autoVJStep', { step: testIdSlug(stepId) }),
  /** One parameter control under a prefix (`packagePrefix`, `audioLayer`, …). */
  control: (prefix: string, key: string): string => testIdOf('control', { prefix, key }),
  /** One parameter control of an effect package's own panel. */
  packageControl: (packageId: string, key: string): string => testIdOf('control', { prefix: testIdSlug(packageId), key }),
  /** An effect package's saved-look list. */
  presetList: (packageId: string): string => testIdOf('presetList', { prefix: testIdSlug(packageId) }),
} as const;

export type TestIdBuilder = keyof typeof testIdFor;

/**
 * Test ids the console has RETIRED: journeys prove they never render again. Declared here so an absence
 * check names the retired id through the registry too; R2 does not apply to them.
 */
export const RETIRED_TEST_IDS = {
  effectPreset: 'effect-preset',
  audioPresetTempoRange: 'audio-preset-tempo-range',
  autoVjNowNext: 'auto-vj-now-next',
  contextController: 'context-controller',
} as const;

export const RETIRED_TEST_ID_TEMPLATES = {
  /** The per-card Baseline / Reactive add buttons, retired by the collapsed audio card. */
  effectCardAdd: 'select-{package}-add-{zone}',
} as const;

/** How a CSS selector names a test id: `{match}` is `=`, `^=` (starts with) or `$=` (ends with). */
export const TEST_ID_SELECTOR = '[data-testid{match}"{id}"]';

/** `[data-testid="…"]` for an id — the one way a CSS selector names a test id. */
export function testIdSelector(id: string, match: '=' | '^=' | '$=' = '='): string {
  return fillTestId(TEST_ID_SELECTOR, { match, id });
}

/**
 * The few class names a live plan's selector reads (a pane row, a tree row, the Auto VJ set row). A
 * component renders these classes from here, so the selector and the markup cannot drift apart.
 */
export const CLASS_NAMES = {
  selected: 'selected',
  settingsTreeRow: 'settings-tree-row',
  settingsRootRow: 'settings-root-row',
  autoVJSetRow: 'auto-vj-set-row',
  autoVJSetOpen: 'auto-vj-set-open',
} as const;

const layerHead = TEST_ID_TEMPLATES.audioLayer.slice(0, TEST_ID_TEMPLATES.audioLayer.indexOf('{'));
const controlTail = (key: string): string => `-${key}`;

/**
 * The CSS selectors live plans declare, composed only from the ids, templates and classes above. A plan
 * names one by key (`SELECTORS.audioStackRows`); the generated JSON carries the composed string.
 */
export const SELECTORS = {
  /** Every layer row in the Audio reactive card's pane list. */
  audioStackRows: `${testIdSelector(TEST_IDS.audioStackLayers)} .${CLASS_NAMES.settingsRootRow}`,
  /** The pane's first row: the baseline, which always leads the list. */
  audioStackFirstRow: `${testIdSelector(TEST_IDS.audioStackLayers)} li:first-child .${CLASS_NAMES.settingsRootRow}`,
  /** The pane's last row: the layer added most recently. */
  audioStackLastRow: `${testIdSelector(TEST_IDS.audioStackLayers)} li:last-child .${CLASS_NAMES.settingsRootRow}`,
  /** A layer's reaction-mode control (a built-in reaction's own parameter). */
  audioLayerMode: `${testIdSelector(layerHead, '^=')}${testIdSelector(controlTail('mode'), '$=')}`,
  /** A layer's beat-lock control. */
  audioLayerSyncControl: `${testIdSelector(layerHead, '^=')}${testIdSelector(
    TEST_ID_TEMPLATES.syncControl.slice(TEST_ID_TEMPLATES.syncControl.indexOf('}') + 1), '$=')}`,
  /** The speed control of the layer the sheet has selected. */
  sheetLayerSpeed: `${testIdSelector(TEST_IDS.settingsSheetDetail)} input${testIdSelector(controlTail('speed'), '$=')}`,
  /** The Auto VJ set the card has loaded, by the name that opens it. */
  autoVJOpenedSet: `.${CLASS_NAMES.autoVJSetRow}.${CLASS_NAMES.selected} .${CLASS_NAMES.autoVJSetOpen}`,
  /** The Auto VJ set the room has marked active. */
  autoVJActiveSet: `${testIdSelector(TEST_IDS.autoVjSetOpen)}[data-active="true"]`,
} as const;

/** Selector shapes that carry a plan's own value (a tree row's depth and name, an option's value). */
export const SELECTOR_TEMPLATES = {
  treeItem: '[role="treeitem"][aria-level="{level}"]',
  treeItemNamed: '[aria-label="{name}"]',
  treeRow: ` > .${CLASS_NAMES.settingsTreeRow}`,
  option: ' option[value="{value}"]',
} as const;

/** The clickable row of a tree item at a depth (1 = a root), by its accessible name or the first one. */
export function treeRowSelector(level: number, name?: string): string {
  return fillTestId(SELECTOR_TEMPLATES.treeItem, { level })
    + (name === undefined ? '' : fillTestId(SELECTOR_TEMPLATES.treeItemNamed, { name }))
    + SELECTOR_TEMPLATES.treeRow;
}

/** One `<option>` of a picker addressed by its test id. */
export function optionSelector(selectTestId: string, value: string): string {
  return testIdSelector(selectTestId) + fillTestId(SELECTOR_TEMPLATES.option, { value });
}
